import { useCallback, useLayoutEffect, useRef, type ReactNode } from 'react'
import { PULL_TRIGGER_DISTANCE } from '../pullGesture'
import {
  MAX_FRAME_MS,
  SPIN_DOWN_TAU,
  SPIN_REST_OMEGA,
  SPIN_TARGET_OMEGA,
  SPIN_UP_TAU,
  SPRING_RELEASE,
  SPRING_SETTLE,
  pullAngle,
  springAt,
  stepAngle,
  stepSpring,
  type AngleState,
  type SpringConfig,
  type SpringState,
} from '../pullMotion'
import { usePullToRefresh } from '../usePullToRefresh'
import './PullToRefresh.css'

interface PullToRefreshProps {
  /** Runs on a committed pull; the indicator stays up until it settles. */
  onRefresh: () => Promise<void> | void
  /** Off while another gesture owns the screen. */
  enabled?: boolean
  children: ReactNode
}

/** Ring geometry. 30px box, r 12.5, so the circumference below is 2π × 12.5. */
const RING_CIRCUMFERENCE = 78.54
/** The indeterminate segment shown while the request is in flight — 28% of the ring. */
const REFRESH_ARC = 22
/** The ring is fully opaque by this fraction of the pull, so it is present as soon as the
 *  gesture is rather than fading in for the first third of it. */
const OPACITY_FULL_AT = 0.45

/**
 * Wraps a screen in the pull and its indicator; the wrapped content is untouched — the screens
 * that pull don't know they can be.
 *
 * Everything that moves is written straight to the DOM from one rAF loop rather than through
 * React state. The rotation in particular cannot be a CSS keyframe: it has to continue from the
 * angle *and speed* the finger left the ring at, which no `animation` can start from.
 */
export default function PullToRefresh({
  onRefresh,
  enabled = true,
  children,
}: PullToRefreshProps) {
  const { containerRef, pull } = usePullToRefresh({ onRefresh, enabled })

  const contentRef = useRef<HTMLDivElement | null>(null)
  const indicatorRef = useRef<HTMLDivElement | null>(null)
  const spinRef = useRef<HTMLSpanElement | null>(null)
  const headRef = useRef<SVGCircleElement | null>(null)

  const motion = useRef<{
    offset: SpringState
    spin: AngleState
    target: number
    spring: SpringConfig
    spinning: boolean
    frame: number | null
    last: number
  }>({
    offset: springAt(0),
    spin: { angle: 0, omega: 0 },
    target: 0,
    spring: SPRING_SETTLE,
    spinning: false,
    frame: null,
    last: 0,
  })

  /** Write the current motion state to the DOM. The dash and the opacity both ride the offset, so
   *  a retracting spring shrinks the ring and fades it out for free. */
  const paint = useCallback(() => {
    const m = motion.current
    const offset = m.offset.value
    const progress = offset / PULL_TRIGGER_DISTANCE

    // `none`, not `translateY(0)`: any transform — an identity one included — makes this element a
    // containing block for `position: fixed` descendants, which is what trapped modals inside the
    // page slot. The wrapped screens render their own sheets and confirms, so the
    // transform may only exist while a pull is actually in progress.
    if (contentRef.current) {
      contentRef.current.style.transform = offset > 0 ? `translateY(${offset}px)` : 'none'
    }
    if (indicatorRef.current) {
      indicatorRef.current.style.transform = `translateY(${offset}px)`
      indicatorRef.current.style.opacity = String(Math.min(1, progress / OPACITY_FULL_AT))
    }
    if (spinRef.current) {
      spinRef.current.style.transform = `rotate(${m.spin.angle}deg)`
    }
    if (headRef.current) {
      const arc = m.spinning ? REFRESH_ARC : Math.min(1, progress) * RING_CIRCUMFERENCE
      headRef.current.style.strokeDasharray = `${arc} ${RING_CIRCUMFERENCE}`
    }
  }, [])

  /** The one frame loop. It reschedules itself for as long as anything is still moving: the
   *  offset spring, the wind-down of the ring, or a request still in flight. */
  const startFrames = useCallback(() => {
    const m = motion.current
    if (m.frame !== null) return
    m.last = performance.now()

    function runFrame(time: number) {
      m.frame = null
      const dt = Math.min(MAX_FRAME_MS, time - m.last)
      m.last = time

      m.offset = stepSpring(m.offset, m.target, m.spring, dt)
      m.spin = stepAngle(
        m.spin,
        dt,
        m.spinning ? SPIN_TARGET_OMEGA : 0,
        m.spinning ? SPIN_UP_TAU : SPIN_DOWN_TAU
      )
      paint()

      // While the request is in flight the ring keeps turning even though the offset has settled.
      if (m.spinning || !m.offset.done || m.spin.omega > SPIN_REST_OMEGA) {
        m.frame = requestAnimationFrame(runFrame)
      }
    }

    m.frame = requestAnimationFrame(runFrame)
  }, [paint])

  const stopFrames = useCallback(() => {
    const m = motion.current
    if (m.frame !== null) {
      cancelAnimationFrame(m.frame)
      m.frame = null
    }
  }, [])

  const { phase, distance, elastic, releaseOmega } = pull

  useLayoutEffect(() => {
    const m = motion.current

    // Reduced motion keeps the behaviour: no elastic travel, no spin. The indicator snaps in at
    // its resting offset and out again, and the content underneath never moves.
    if (!elastic) {
      stopFrames()
      m.offset = springAt(phase === 'idle' ? 0 : PULL_TRIGGER_DISTANCE)
      m.spin = { angle: 0, omega: 0 }
      m.spinning = phase === 'refreshing'
      if (contentRef.current) contentRef.current.style.transform = 'none'
      if (indicatorRef.current) {
        indicatorRef.current.style.transform = `translateY(${m.offset.value}px)`
        indicatorRef.current.style.opacity = phase === 'idle' ? '0' : '1'
      }
      if (spinRef.current) spinRef.current.style.transform = 'none'
      if (headRef.current) {
        // A static 28% arc would read as broken with nothing turning it, so the ring shows whole.
        const arc =
          phase === 'refreshing'
            ? RING_CIRCUMFERENCE
            : Math.min(1, distance / PULL_TRIGGER_DISTANCE) * RING_CIRCUMFERENCE
        headRef.current.style.strokeDasharray = `${arc} ${RING_CIRCUMFERENCE}`
      }
      return
    }

    if (phase === 'pulling') {
      // Finger-driven: no spring, no integrator, and no rounding of the angle.
      stopFrames()
      m.offset = springAt(distance)
      m.spin = { angle: pullAngle(distance), omega: 0 }
      m.spinning = false
      m.target = distance
      paint()
      return
    }

    if (phase === 'refreshing') {
      m.target = PULL_TRIGGER_DISTANCE
      m.spring = SPRING_RELEASE
      m.spinning = true
      // The handover that removes the seam: keep the angle the finger wound on and carry its
      // speed, then let the integrator ease that to the steady rate.
      m.spin = { angle: m.spin.angle, omega: releaseOmega }
      startFrames()
      return
    }

    m.target = 0
    m.spring = SPRING_SETTLE
    m.spinning = false
    if (m.offset.done && m.offset.value === 0 && m.spin.omega <= SPIN_REST_OMEGA) {
      // Idle from the start — nothing to animate, just establish the resting styles.
      paint()
      return
    }
    startFrames()
  }, [phase, distance, elastic, releaseOmega, paint, startFrames, stopFrames])

  useLayoutEffect(() => stopFrames, [stopFrames])

  return (
    <div className="pull-refresh" ref={containerRef}>
      <div
        className="pull-refresh__indicator"
        ref={indicatorRef}
        data-phase={pull.phase}
        data-armed={pull.armed ? 'true' : 'false'}
        role="status"
        aria-live="polite"
      >
        <span className="pull-refresh__ring">
          <span className="pull-refresh__spin" ref={spinRef}>
            <svg className="pull-refresh__svg" viewBox="0 0 30 30" aria-hidden="true">
              <circle className="pull-refresh__track" cx="15" cy="15" r="12.5" />
              <circle
                className="pull-refresh__head"
                ref={headRef}
                cx="15"
                cy="15"
                r="12.5"
                transform="rotate(-90 15 15)"
              />
            </svg>
          </span>
        </span>
        <span className="pull-refresh__sr">
          {pull.phase === 'refreshing' ? 'Refreshing' : ''}
        </span>
      </div>
      <div className="pull-refresh__content" ref={contentRef}>
        {children}
      </div>
    </div>
  )
}
